/**
 * Named handles for the capability keys, so callers pass `FEATURES.VEHICLE_DETAILS`
 * instead of a bare `'vehicleDetails'` literal — a typo becomes a compile error at the
 * call site rather than a feature that silently never matches.
 *
 * This is the single source of truth for *which* capabilities exist. Every key of
 * CAPABILITY_SCHEMA below is computed from it, so the two can never drift: adding an
 * entry here is a compile error until the schema declares its subfeatures.
 *
 * Most values are a nav entity key from `getEntitiesInitialState()`
 * (sales-checkin-store/src/showroomNav/initialState.ts) — see
 * docs/ai-gen/f303238-edition-based-capability-configuration/capability-model.md
 * for the full mapping and rationale. Behavioural capabilities that gate no nav item
 * (e.g. `gtl`, enforced in middleware) also live here, so a key being absent from the
 * nav entities is not a bug.
 */
export const FEATURES = {
    BUYER: 'buyer',
    BUYER_INBOX: 'buyerInbox',
    ADD_COBUYER: 'addCobuyer',
    COBUYER: 'cobuyer',
    COBUYER_INBOX: 'cobuyerInbox',
    SEND_TO_DESK: 'sendToDesk',
    DEAL_COMMENTS: 'dealComments',
    SHARE_DROPDOWN: 'shareDropdown',
    CREDIT_APP: 'creditApp',
    DOC_UPLOAD: 'docUpload',
    DEAL_PDF: 'dealPdf',
    VEHICLE_DETAILS: 'vehicleDetails',
    TRADE_IN: 'tradeIn',
    CUSTOMER_DETAILS: 'customerDetails',
    DEAL_SUMMARY: 'dealSummary',
    BROWSE_INVENTORY: 'browseInventory',
    MANAGE_DOCUMENTS: 'manageDocuments',
    RECENT_DEALS: 'recentDeals',
    INSURANCE: 'insurance',
    GTL: 'gtl',
    GLOBAL: 'global',
    // Edition marker, not a surface: FULL only for DC Sales Desking, so workflow rules can
    // tell Desking apart from the showroom family without reading the dealer's edition setting (US2079533).
    SALES_VIEW_REDIRECTION: 'salesViewRedirection'
} as const;

/**
 * Which subfeatures each capability can have. Keys come from FEATURES, so the schema
 * cannot name a capability that has no handle; the `satisfies` closes the loop in the
 * other direction by requiring an entry for every handle.
 *
 * The key type is spelled out inline rather than imported as `FeatureName` from
 * `./types`, which would make constants and types import each other.
 */
export const CAPABILITY_SCHEMA = {
    [FEATURES.BUYER]: [],
    [FEATURES.BUYER_INBOX]: [],
    [FEATURES.ADD_COBUYER]: [],
    [FEATURES.COBUYER]: [],
    [FEATURES.COBUYER_INBOX]: [],
    [FEATURES.SEND_TO_DESK]: [],
    [FEATURES.DEAL_COMMENTS]: [],
    [FEATURES.SHARE_DROPDOWN]: [],
    [FEATURES.CREDIT_APP]: [],
    [FEATURES.DOC_UPLOAD]: [],
    [FEATURES.DEAL_PDF]: [],
    [FEATURES.VEHICLE_DETAILS]: ['images', 'details', 'optionalEquipment', 'estimatedMPG', 'warranty'],
    [FEATURES.TRADE_IN]: [],
    [FEATURES.CUSTOMER_DETAILS]: [],
    [FEATURES.DEAL_SUMMARY]: [],
    [FEATURES.BROWSE_INVENTORY]: [],
    [FEATURES.MANAGE_DOCUMENTS]: [],
    [FEATURES.RECENT_DEALS]: [],
    [FEATURES.INSURANCE]: [],
    [FEATURES.GTL]: [],
    [FEATURES.GLOBAL]: [],
    [FEATURES.SALES_VIEW_REDIRECTION]: []
} as const;
